import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { BasicFormFieldProps, DropdownField, NumberField, Text } from '@frontend/design-system';

type DurationTimeUnitSelectionProps = {
  durationFieldProps: Omit<BasicFormFieldProps<'number'>, 'label' | 'name'>;
  timeUnitFieldProps: Omit<BasicFormFieldProps<'dropdown'>, 'label' | 'children' | 'name'>;
  trailText?: string;
  errorText?: string;
};

export const DurationTimeUnitSelection = ({
  durationFieldProps,
  timeUnitFieldProps,
  trailText,
  errorText,
}: DurationTimeUnitSelectionProps) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <div css={wrapperStyle}>
      <div css={rowStyle}>
        <div css={inputsStyle}>
          <NumberField name='duration' {...durationFieldProps} label={t('Duration')} css={durationFieldStyle} />
          <DropdownField name='timeUnit' {...timeUnitFieldProps} label={t('Time Unit')} css={timeUnitFieldStyle}>
            <DropdownField.Option value='hours'>{t('Hours')}</DropdownField.Option>
            <DropdownField.Option value='days'>{t('Days')}</DropdownField.Option>
          </DropdownField>
        </div>
        {trailText && (
          <Text color='subdued' css={trailTextStyle}>
            {trailText}
          </Text>
        )}
      </div>
      {Number(durationFieldProps.value) > 0 && errorText && (
        <Text color='error' css={errorTextStyle}>
          {errorText}
        </Text>
      )}
    </div>
  );
};

const wrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-xs)',
});

const rowStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--fabric-space-gap-sm)',
  alignItems: 'center',
  [`@media (max-width: ${breakpoints.small.max}px)`]: { alignItems: 'end' },
});

const durationFieldStyle = css({ width: 100 });

const timeUnitFieldStyle = css({ width: 160, textTransform: 'capitalize' });

const inputsStyle = css({
  display: 'flex',
  flexShrink: 0,
  gap: 'var(--fabric-space-gap-sm)',
  alignItems: 'center',
  [`@media (max-width: ${breakpoints.small.max}px)`]: { flexDirection: 'column', alignItems: 'flex-start' },
});

const trailTextStyle = css({ flexShrink: 1 });

const errorTextStyle = css({ fontSize: 'var(--fabric-typography-body-xs-font-size)' });
